/**
 * Version history for a submitted review — human re-submits and Ace re-ratings.
 */

import { useEffect, useState } from "react";
import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronRight,
  faClockRotateLeft,
  faRobot,
  faUser,
} from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import LoadingPlaceholder from "@/components/ui/loading-placeholder/LoadingPlaceholder";
import { agentReviewAPI, type ReviewVersionEntry } from "@/services/api/agentReview";
import { ragOption } from "./RagOption";
import type { Rag } from "./types";

interface ReviewVersionHistoryProps {
  reviewId: string | null;
  currentVersion?: number;
  refreshToken?: number;
}

function phaseRag(
  entry: ReviewVersionEntry,
  phase: "orchestration" | "output",
): { human: Rag | null; ace: Rag | null } {
  const rating = entry.snapshot.ratings.find((r) => r.step_type === phase);
  const human = rating?.human_rag ?? null;
  const ace =
    phase === "orchestration"
      ? entry.snapshot.ace_rating?.orchestration?.suggested_rag ?? null
      : entry.snapshot.ace_rating?.output?.suggested_rag ?? null;
  return { human, ace };
}

function RagBadge({ rag, prefix }: { rag: Rag | null; prefix: string }) {
  if (!rag) {
    return (
      <span className="text-[11px] text-slate-500">
        {prefix}: —
      </span>
    );
  }
  const opt = ragOption(rag);
  return (
    <span className={clsx("inline-flex items-center gap-1 text-[11px] font-medium", opt.text)}>
      <span className={clsx("w-1.5 h-1.5 rounded-full", opt.dot)} aria-hidden />
      {prefix}: {opt.label}
    </span>
  );
}

function changeLabel(entry: ReviewVersionEntry): string {
  if (entry.change_type === "ace_rate") {
    return entry.note || "Ace rated";
  }
  return entry.note || "Human re-submitted";
}

function formatWhen(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export function ReviewVersionHistory({ reviewId, currentVersion, refreshToken = 0 }: ReviewVersionHistoryProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [versions, setVersions] = useState<ReviewVersionEntry[]>([]);

  useEffect(() => {
    if (!reviewId || !open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    void agentReviewAPI
      .listReviewVersions(reviewId)
      .then((rows) => {
        if (!cancelled) setVersions(rows);
      })
      .catch(() => {
        if (!cancelled) setError("Couldn't load version history.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reviewId, open, refreshToken]);

  if (!reviewId) return null;

  return (
    <div className="rounded-xl ring-1 ring-white/10 bg-white/[0.02] overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-white/[0.02]"
      >
        <FontAwesomeIcon icon={faClockRotateLeft} className="text-slate-400 text-sm shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-slate-200">Version history</p>
          <p className="text-xs text-slate-500 mt-0.5">
            {currentVersion ? `Current v${currentVersion}` : "Track changes over time"}
            {versions.length > 0 ? ` · ${versions.length} prior snapshot${versions.length === 1 ? "" : "s"}` : ""}
          </p>
        </div>
        <FontAwesomeIcon
          icon={open ? faChevronDown : faChevronRight}
          className="text-slate-500 text-xs shrink-0"
        />
      </button>

      {open ? (
        <div className="border-t border-white/5 px-4 py-3 space-y-2">
          {loading ? <LoadingPlaceholder text="Loading history…" /> : null}
          {error ? <p className="text-sm text-rose-400">{error}</p> : null}
          {!loading && !error && versions.length === 0 ? (
            <p className="text-sm text-slate-500">No prior versions — this is the first rating.</p>
          ) : null}
          {!loading && !error
            ? versions.map((entry) => {
                const orch = phaseRag(entry, "orchestration");
                const out = phaseRag(entry, "output");
                return (
                  <div
                    key={entry.version_id}
                    className="rounded-lg bg-white/[0.02] px-3 py-2.5 ring-1 ring-white/5 space-y-1.5"
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded-full bg-slate-500/15 px-2 py-0.5 text-[11px] font-medium text-slate-300 ring-1 ring-slate-500/20 tabular-nums">
                        v{entry.version_number}
                      </span>
                      <FontAwesomeIcon
                        icon={entry.change_type === "ace_rate" ? faRobot : faUser}
                        className="text-[10px] text-slate-500"
                      />
                      <span className="text-xs text-slate-300">{changeLabel(entry)}</span>
                      <span className="text-[11px] text-slate-500 ml-auto">{formatWhen(entry.recorded_at)}</span>
                    </div>
                    {entry.criteria_version_label ? (
                      <p className="text-[11px] text-slate-500">
                        Criteria: {entry.criteria_version_label}
                      </p>
                    ) : null}
                    <div className="flex flex-wrap gap-x-4 gap-y-1 pt-0.5">
                      <RagBadge rag={orch.human} prefix="Orch human" />
                      <RagBadge rag={orch.ace} prefix="Orch Ace" />
                      <RagBadge rag={out.human} prefix="Out human" />
                      <RagBadge rag={out.ace} prefix="Out Ace" />
                    </div>
                  </div>
                );
              })
            : null}
        </div>
      ) : null}
    </div>
  );
}
